import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useMemo } from 'react'

import type { RegistryKind } from '@/api/actions'
import { Button } from '@/components/ui/button'

import { blankAction, blankRule } from './doc'
import { DocEditor } from './DocEditor'

/**
 * `/actions?new=action|rule` — make one by hand from a sensible blank (one character doing one
 * move; or "when a thing lands in a place, it splashes"). Saving creates it (`POST`) and opens it
 * in the editor, where the preview plays it at once.
 */

const justCreated = new Set<string>()

/** Remember that `id` was just made here, so its page plays the preview straight away. */
export function markJustCreated(kind: RegistryKind, id: string): void {
  justCreated.add(`${kind}:${id}`)
}

/** True once for a document `markJustCreated` named. */
export function consumeJustCreated(kind: RegistryKind, id: string): boolean {
  return justCreated.delete(`${kind}:${id}`)
}

export function NewDocPage({ kind, onCancel, validateDelay }: { kind: RegistryKind; onCancel(): void; validateDelay?: number }) {
  const navigate = useNavigate()
  const blank = useMemo(() => (kind === 'rule' ? blankRule() : blankAction()), [kind])
  const tab = kind === 'rule' ? 'rules' : 'actions'
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8" data-testid={`new-${kind}`}>
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/actions" search={{ tab }}>
            <ArrowLeft aria-hidden />
            {kind === 'rule' ? 'All world rules' : 'All actions'}
          </Link>
        </Button>
      </div>
      <header className="flex max-w-3xl flex-col gap-1">
        <h1 className="text-3xl font-semibold tracking-tight text-ink">{kind === 'rule' ? 'New world rule' : 'New action'}</h1>
        <p className="text-ink-2">
          {kind === 'rule'
            ? 'Say when it happens, between what, and what the world does then. Start from the example and change it.'
            : 'Give it a name, say who takes part, then what happens step by step. Start from the example and change it.'}
        </p>
      </header>
      <DocEditor
        kind={kind}
        mode="new"
        doc={blank}
        validateDelay={validateDelay}
        onClose={onCancel}
        onSaved={(doc) => {
          markJustCreated(kind, doc.id)
          // In place of the blank: Back then goes to the list, not to an empty "New" form.
          void navigate({ to: '/actions/$id', params: { id: doc.id }, search: { kind, edit: 1 }, replace: true })
        }}
      />
    </div>
  )
}
